/**
 * V1 Foundational Lift Pool — Exactly 5 lifts.
 * Per V1 Product Spec: DUEL measures strength as generalized, bodyweight-normalized
 * performance across foundational lifts. Each lift contributes ≤25% of total score.
 *
 * Changes to this pool require constitutional review.
 */

export const V1_LIFT_POOL = [
  'back-squat',
  'deadlift',
  'bench-press',
  'weighted-pull-up',
  'dead-hang',
] as const;

export type V1Lift = (typeof V1_LIFT_POOL)[number];

/** Human-readable labels for the V1 lift pool */
export const V1_LIFT_LABELS: Record<V1Lift, string> = {
  'back-squat': 'Back Squat',
  'deadlift': 'Deadlift',
  'bench-press': 'Bench Press',
  'weighted-pull-up': 'Pull-Ups',
  'dead-hang': 'Dead Hang',
};

/** Input requirements per lift */
export const V1_LIFT_INPUTS: Record<V1Lift, { fields: string[]; unit: string; description: string }> = {
  'back-squat': {
    fields: ['weight', 'reps'],
    unit: 'e1RM',
    description: 'Lower-body squat pattern. Log weight × reps → estimated 1RM.',
  },
  'deadlift': {
    fields: ['weight', 'reps'],
    unit: 'e1RM',
    description: 'Hip hinge pattern. Log weight × reps → estimated 1RM.',
  },
  'bench-press': {
    fields: ['weight', 'reps'],
    unit: 'e1RM',
    description: 'Upper-body push. Log weight × reps → estimated 1RM.',
  },
  'weighted-pull-up': {
    fields: ['bodyweight', 'externalLoad', 'reps'],
    unit: 'e1RM',
    description: 'Upper-body pull. (Bodyweight + external load) × reps → e1RM equivalent.',
  },
  'dead-hang': {
    fields: ['holdTimeSec'],
    unit: 'time',
    description: 'Grip endurance. Hang from a bar as long as possible (seconds).',
  },
};

/** Per-lift contribution cap as fraction of total score */
export const V1_LIFT_CAP = 0.25;

/** Maximum number of lifts in V1 */
export const V1_LIFT_COUNT = 5;

// ---- Legacy compatibility ----
// The old CANONICAL_DUEL_LIFTS only had 3 lifts. Keep for backward compat during migration.
export const CANONICAL_DUEL_LIFTS = ['bench', 'squat', 'deadlift'] as const;
type LegacyCanonical = (typeof CANONICAL_DUEL_LIFTS)[number];

const LEGACY_VARIANTS: Record<LegacyCanonical, string[]> = {
  bench: ['bench-press', 'barbell-bench', 'bb-bench', 'flat-bench', 'benchpress', 'barbell bench press'],
  squat: ['back-squat', 'barbell-squat', 'bb-squat', 'barbell back squat'],
  deadlift: ['dead-lift', 'conventional-deadlift', 'barbell-deadlift', 'bb-deadlift', 'conventional deadlift'],
};

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function normalizeLiftKey(raw: string): { canonical: LegacyCanonical | null; normalizedRaw: string } {
  const normalizedRaw = slugify(String(raw || ''));
  if (CANONICAL_DUEL_LIFTS.includes(normalizedRaw as LegacyCanonical)) {
    return { canonical: normalizedRaw as LegacyCanonical, normalizedRaw };
  }
  for (const c of CANONICAL_DUEL_LIFTS) {
    const variants = LEGACY_VARIANTS[c] || [];
    for (const v of variants) {
      if (normalizedRaw === slugify(v)) return { canonical: c, normalizedRaw };
    }
  }
  return { canonical: null, normalizedRaw };
}

/**
 * Normalize any exercise name/key to a V1 lift, or return null if not in the V1 pool.
 */
export function normalizeToV1Lift(raw: string): V1Lift | null {
  const slug = slugify(String(raw || ''));

  // Direct match
  if (V1_LIFT_POOL.includes(slug as V1Lift)) return slug as V1Lift;

  // Variant matching
  const V1_VARIANTS: Record<V1Lift, string[]> = {
    'back-squat': ['squat', 'barbell-squat', 'bb-squat', 'barbell-back-squat', 'back-squat', 'barbell-squat', 'low-bar-squat', 'high-bar-squat'],
    'deadlift': ['dead-lift', 'conventional-deadlift', 'barbell-deadlift', 'bb-deadlift', 'conventional-deadlift', 'sumo-deadlift'],
    'bench-press': ['bench', 'barbell-bench', 'bb-bench', 'flat-bench', 'benchpress', 'barbell-bench-press', 'flat-barbell-bench-press', 'flat-bench-press'],
    'weighted-pull-up': ['weighted-pullup', 'weighted-chin-up', 'pull-up-weighted', 'pullup-weighted', 'pull-ups', 'pull-up', 'pullup', 'pullups', 'chin-up', 'chin-ups', 'chinup', 'chinups'],
    'dead-hang': ['dead-hang', 'deadhang', 'bar-hang', 'passive-hang', 'hang', 'dead-hang-hold', 'bar-hang-hold'],
  };

  for (const lift of V1_LIFT_POOL) {
    const variants = V1_VARIANTS[lift] || [];
    for (const v of variants) {
      if (slug === slugify(v)) return lift;
    }
  }

  // Substring fallback for common exercise names from various DBs
  if (slug.includes('bench') && slug.includes('press')) return 'bench-press';
  if (slug.includes('squat') && !slug.includes('split') && !slug.includes('front') && !slug.includes('goblet') && !slug.includes('safety')) return 'back-squat';
  if (slug.includes('deadlift') && !slug.includes('romanian') && !slug.includes('rdl') && !slug.includes('stiff')) return 'deadlift';
  if (slug.includes('pull-up') || slug.includes('pullup') || slug === 'pull-ups') return 'weighted-pull-up';
  if (slug.includes('dead-hang') || slug.includes('deadhang') || slug === 'hang' || slug.includes('bar-hang') || slug.includes('passive-hang')) return 'dead-hang';

  return null;
}

/**
 * Check if a given exercise name is in the V1 foundational lift pool.
 */
export function isV1Lift(exerciseName: string): boolean {
  return normalizeToV1Lift(exerciseName) !== null;
}

/**
 * Calculate estimated 1RM using Brzycki formula.
 * For reps = 1, returns the weight directly.
 */
export function calculateE1RM(weight: number, reps: number): number {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  if (reps > 12) reps = 12; // Cap at 12 reps for accuracy
  return weight * (36 / (37 - reps));
}

/**
 * Calculate Farmer's Carry score: totalLoad × holdTimeSec
 * @deprecated Use calculateDeadHangScore for V1 scoring. Kept for legacy data.
 */
export function calculateFarmersCarryScore(totalLoad: number, holdTimeSec: number): number {
  if (totalLoad <= 0 || holdTimeSec <= 0) return 0;
  return totalLoad * holdTimeSec;
}

/**
 * Calculate Dead Hang score: scaled seconds value.
 * We scale by a factor that brings dead hang into the same range as barbell e1RM/BW ratios.
 * A 60-second hang is roughly equivalent to a 1.5x BW deadlift in difficulty.
 * Max plausible hang (~300s) should produce a value around 5-6 (comparable to elite deadlift/BW ratio).
 *
 * Scaling examples (normalized = scaledSeconds):
 *   30s  → log1p(1)  * 2 ≈ 1.39
 *   60s  → log1p(2)  * 2 ≈ 2.20
 *   90s  → log1p(3)  * 2 ≈ 2.77
 *  120s  → log1p(4)  * 2 ≈ 3.22
 *  300s  → log1p(10) * 2 ≈ 4.80
 */
export function calculateDeadHangScore(holdTimeSec: number, bodyweight: number): number {
  if (holdTimeSec <= 0 || bodyweight <= 0) return 0;
  const scaledSeconds = Math.log1p(holdTimeSec / 30) * 2;
  return scaledSeconds * bodyweight;
}

/**
 * Normalize a lift score by bodyweight using the Wilks-inspired ratio.
 * Returns score relative to bodyweight (higher = stronger relative to BW).
 */
export function normalizeByBodyweight(liftScore: number, bodyweight: number): number {
  if (bodyweight <= 0 || liftScore <= 0) return 0;
  return liftScore / bodyweight;
}

/**
 * Calculate V1 composite strength score from individual lift scores.
 * Each lift is capped at 25% contribution.
 * All lifts should be BW-normalized before passing in.
 */
export function calculateV1StrengthScore(
  liftScores: Partial<Record<V1Lift, number>>
): { total: number; contributions: Record<V1Lift, number> } {
  const contributions: Record<V1Lift, number> = {
    'back-squat': 0,
    'deadlift': 0,
    'bench-press': 0,
    'weighted-pull-up': 0,
    'dead-hang': 0,
  };

  // Find the maximum possible contribution for capping
  let rawTotal = 0;
  for (const lift of V1_LIFT_POOL) {
    const score = liftScores[lift] ?? 0;
    contributions[lift] = score;
    rawTotal += score;
  }

  if (rawTotal === 0) return { total: 0, contributions };

  // Apply per-lift cap: no single lift can contribute more than 25% of total
  let cappedTotal = 0;
  const maxContribution = rawTotal * V1_LIFT_CAP;

  for (const lift of V1_LIFT_POOL) {
    const capped = Math.min(contributions[lift], maxContribution);
    contributions[lift] = capped;
    cappedTotal += capped;
  }

  return { total: Math.round(cappedTotal * 100), contributions };
}
